/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";

import useStyle from "./styles";
import { MESSAGES, TITLE } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { selectError } from "../../store/error/errorSelector";
import { createModule, editModule } from "../../store/module/module";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { showErrorAction } from "../../store/error/errorActionCreator";
import { selectSelectedModule } from "../../store/module/moduleSelector";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { selectSelectedApplication } from "../../store/application/applicationSelector";
import { selectSelectedOrganization } from "../../store/organization/organizationSelector";

export interface IModuleData {
  id: string;
  name: string;
  description: string;
}

interface IAddModuleDialogProps {
  moduleData?: IModuleData;
  isEdit: boolean;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
  openModal: boolean;
  refreshOnSave: Function;
}

export interface IModuleRequest {
  id?: string;
  name?: string;
  serviceId?: string;
  description?: string;
  moduleKey?: string;
}

const AddModuleDialog = (props: IAddModuleDialogProps) => {
  const { openModal, openCloseModal } = props;
  const classes = useStyle();
  const dispatch = useDispatch();

  const formDefaultValues: IModuleRequest = {
    id: "",
    name: "",
    serviceId: "",
    moduleKey: "",
    description: "",
  };

  const formErrorDefaultValues = {
    errorName: false,
    errorServiceId: false,
    errorModuleKey: false,
    errorDescription: false,
    errorHelperTextName: "",
    errorHelperTextModuleKey: "",
    errorHelperTextServiceId: "",
    errorHelperTextDescription: "",
  };

  const errorMesaage = useSelector(selectError);
  const selectedModule = useSelector(selectSelectedModule);
  const selectedApplication = useSelector(selectSelectedApplication);
  const selectedOrganization = useSelector(selectSelectedOrganization);

  const [showLoader, showHideLoader] = useState(false);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<IModuleRequest>(formDefaultValues);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.openModal]);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    showHideLoader(false);
    if (openModal && props.isEdit) {
      updateFormValues({ ...props.moduleData });
    }
  }, [openModal, props.isEdit, props.moduleData]);

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    const trimmedValue = value.trim();
    if (name === 'moduleKey') {
      let errorObj = { errorModuleKey: false, errorHelperTextModuleKey: '' };

      if (trimmedValue === '') {
        updateFormValues({ ...formValues, moduleKey: '' });
      } else if (trimmedValue.length > 8) {
        errorObj = {
          errorModuleKey: true,
          errorHelperTextModuleKey: MESSAGES.MAX_MODULE_KEY_LENGTH,
        };
      } else if (!MESSAGES.MODULE_KEY_REGEX.test(trimmedValue)) {
        errorObj = {
          errorModuleKey: true,
          errorHelperTextModuleKey: MESSAGES.MODULE_KEY_ALLOWED_CHARACTERS,
        };
      } else {
        updateFormValues({ ...formValues, moduleKey: trimmedValue });
      }
      updateFormError({ ...formErrorState, ...errorObj });
    } else {
      updateFormValues({ ...formValues, [name]: value });
      updateFormError({ ...formErrorDefaultValues });
    }
  };

  const validateRequired = (values: any) => {
    if (!values.name) {
      updateFormError({
        ...formErrorState,
        errorName: true,
        errorHelperTextName: MESSAGES.MODULE_NAME_REQUIRED
      });
      return false;
    } else if (!values.serviceId) {
      updateFormError({
        ...formErrorState,
        errorServiceId: true,
        errorHelperTextServiceId: MESSAGES.SERVICE_ID_REQUIRED
      });
      return false;
    } else if (!+values.serviceId) {
      updateFormError({
        ...formErrorState,
        errorServiceId: true,
        errorHelperTextServiceId: MESSAGES.SERVICE_ID_INVALID
      });
      return false;
    } else if (!values.moduleKey || !/^[a-z]+$/.test(values.moduleKey)) {
      updateFormError({
        ...formErrorState,
        errorServiceId: true,
        errorHelperTextServiceId: MESSAGES.MODULE_KEY_INVALID
      });
      return false;
    } else if (!values.description) {
      updateFormError({
        ...formErrorState,
        errorDescription: true,
        errorHelperTextDescription: MESSAGES.DESCRIPTION_REQUIRED
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (validateRequired(formValues)) {
      const payload: any = {
        name: formValues.name,
        serviceId: formValues.serviceId,
        moduleKey: props.isEdit ? selectedModule.moduleKey : formValues.moduleKey,
        description: formValues.description,
        applicationId: selectedApplication?.id,
        organizationId: selectedOrganization?.id,
        urlPrefix: selectedModule.urlPrefix || '',
        ...(props.isEdit ? { id: selectedModule.id } : {}),
      };
      showHideLoader(true);
      const isSuccess = props.isEdit
        ? await editModule(dispatch, payload)
        : await createModule(dispatch, payload);

      if (isSuccess) handleClose();
      showHideLoader(false);
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            onClick={handleClose}
            variant="outlined"
          >
            {TITLE.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            disabled={showLoader}
            name="createCustomerBtn"
            onClick={handleSubmit}
            variant="contained"
          >
            {TITLE.SAVE}
          </Button>
        }
        openModal={openModal}
        handleClose={handleClose}
        dialogTitle={props.isEdit ? TITLE.EDIT_MODULE : TITLE.ADD_MODULE}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="name"
            name="name"
            variant="standard"
            disabled={showLoader}
            value={formValues.name}
            label={TITLE.MODULE_NAME}
            onChange={updateFormFields}
            error={formErrorState.errorName}
            className={classes.modalTextBox}
            helperText={formErrorState.errorHelperTextName}
          />
          <CustomTooltip title={MESSAGES.MODULE_NAME_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="serviceId"
            name="serviceId"
            variant="standard"
            label={TITLE.SERVICE_ID}
            value={formValues.serviceId}
            onChange={updateFormFields}
            className={classes.modalTextBox}
            disabled={showLoader || props.isEdit}
            error={formErrorState.errorServiceId}
            helperText={formErrorState.errorHelperTextServiceId}
          />
          <CustomTooltip title={MESSAGES.SERVICE_ID_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="moduleKey"
            name="moduleKey"
            variant="standard"
            label={TITLE.MODULE_KEY}
            value={formValues.moduleKey}
            onChange={updateFormFields}
            className={classes.modalTextBox}
            disabled={showLoader || props.isEdit}
            error={formErrorState.errorModuleKey}
            helperText={formErrorState.errorHelperTextModuleKey}
          />
          <CustomTooltip title={MESSAGES.MODULE_KEY_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="description"
            name="description"
            variant="standard"
            disabled={showLoader}
            label={TITLE.DESCRIPTION}
            onChange={updateFormFields}
            value={formValues.description}
            className={classes.modalTextBox}
            error={formErrorState.errorDescription}
            helperText={formErrorState.errorHelperTextDescription}
          />
          <CustomTooltip title={MESSAGES.DESCRIPTION_INFO}></CustomTooltip>
        </Box>
        {/* {errorMesaage && <ErrorMessage message={errorMesaage.message} />} */}
        <Loader showLoader={showLoader} isCenter />
      </FormDialog>
    </Fragment>
  );
};

export default AddModuleDialog;
